import type { ActivityRow } from "./db";

export function dateKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * Reconcile the two places review activity is recorded: the fast,
 * localStorage-backed `state.activity` (store.tsx's `MetaState`, persisted
 * on every render) and the durable Dexie `db.activity` table. The durable counter is committed atomically with the review event by
 * gradeCardState; the display cache is updated by bumpActivity() ;
 * so if the localStorage copy ever lagged or lost an entry (a tab closed
 * before its persist `useEffect` ran, storage eviction, ...) the
 * streak/heatmap would silently under-report a day Dexie still has a
 * record of. Takes the higher reviewed/correct count per date so neither
 * copy's history can "win" with less data than the other.
 */
export function mergeActivity(...sources: ActivityRow[][]): ActivityRow[] {
  const byDate = new Map<string, ActivityRow>();
  for (const list of sources) {
    for (const entry of list) {
      const existing = byDate.get(entry.date);
      byDate.set(
        entry.date,
        existing
          ? {
              date: entry.date,
              reviewed: Math.max(existing.reviewed, entry.reviewed),
              correct: Math.max(existing.correct, entry.correct),
            }
          : { ...entry },
      );
    }
  }
  return [...byDate.values()];
}

/**
 * Consecutive-day review streak, with a one-day grace period: if today has
 * no reviews yet, a streak ending yesterday still counts as alive — it only
 * breaks once a full day passes with nothing logged. `now` is injectable
 * for tests; production callers leave it as `Date.now()`.
 */
export function computeStreak(activity: ActivityRow[], now: number = Date.now()): number {
  if (activity.length === 0) return 0;
  const byDate = new Map(activity.map((a) => [a.date, a.reviewed]));
  let streak = 0;
  const cursor = new Date(now);
  // If today had no reviews, the streak still includes yesterday-and-back.
  if (!byDate.get(dateKey(cursor))) {
    cursor.setDate(cursor.getDate() - 1);
  }
  while (byDate.get(dateKey(cursor))) {
    streak += 1;
    cursor.setDate(cursor.getDate() - 1);
  }
  return streak;
}

/**
 * Locale-aware short "day + month" label for a `dateKey`-formatted string
 * (e.g. under the 14-day heatmap). Parses the y/m/d components directly
 * instead of `new Date(dateStr)` so the label isn't shifted a day off by
 * UTC parsing in timezones behind/ahead of UTC.
 */
export function formatHeatmapDateLabel(dateStr: string, locale: string): string {
  const [year, month, day] = dateStr.split("-").map(Number);
  return new Intl.DateTimeFormat(locale, { day: "2-digit", month: "2-digit" }).format(
    new Date(year, month - 1, day),
  );
}

export function build14DayHeatmap(
  activity: ActivityRow[],
  now: number = Date.now(),
): { date: string; reviewed: number; isToday: boolean }[] {
  const byDate = new Map(activity.map((a) => [a.date, a.reviewed]));
  const today = dateKey(new Date(now));
  const out: { date: string; reviewed: number; isToday: boolean }[] = [];
  for (let i = 13; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    const k = dateKey(d);
    out.push({ date: k, reviewed: byDate.get(k) ?? 0, isToday: k === today });
  }
  return out;
}
